import {
  type HTMLAttributes,
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { DropResult } from '@hello-pangea/dnd';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { Button, ButtonGroup, Tooltip } from '@heroui/react';
import { ArrowLeft, ArrowUpDown, Clock, FileCode2, SortAsc } from 'lucide-react';

import { type PageChrome, useSetPageChrome } from '~/contexts/PageChromeContext';
import { useProduct } from '~/hooks/useProducts';
import { useProjectOperations } from '~/hooks/useProjectOperations';
import { useProject } from '~/hooks/useProjects';
import { useTaskOperations } from '~/hooks/useTaskOperations';
import { useTasks } from '~/hooks/useTasks';
import { useUIStore } from '~/stores/uiStore';
import { logger } from '~/utils/logger';
import { pluralize } from '~/utils/pluralize';
import { STATUS_DISPLAY } from '~/utils/status';

import { Board } from '~/components/board/Board';
import { BoardColumn } from '~/components/board/BoardColumn';
import { DocumentViewerDialog } from '~/components/DocumentViewerDialog';
import { Feedback } from '~/components/Feedback';
import { FilterBar } from '~/components/layout/FilterBar';
import { MAIN_CONTENT_WIDTH } from '~/components/layout/mainContent';

import { TaskStatus } from '~/types';
import type { Product, ProjectWithTasks, TaskProps } from '~/types';

import { ProjectStatusSelect } from './ProjectStatusSelect';
import { TaskCard } from './TaskCard';
import { useTaskFiltering } from './useTaskFiltering';

/** Board columns in display order — mirrors `FILTER_CHIPS`/`DISPLAY_STATUSES` ordering. */
const BOARD_STATUSES: TaskStatus[] = [TaskStatus.TODO, TaskStatus.IN_PROGRESS, TaskStatus.DONE];

interface ProjectViewProps {
  projectId?: string;
}

function ProjectViewSkeleton(): JSX.Element {
  return (
    <div
      aria-busy="true"
      aria-label="Loading project"
      className={`flex h-full flex-col gap-4 overflow-hidden p-6 animate-pulse ${MAIN_CONTENT_WIDTH}`}
      role="status"
    >
      <div className="h-4 w-32 rounded bg-surface-elevated" />
      <div className="flex items-center gap-3">
        <div className="h-6 w-6 rounded bg-surface-elevated" />
        <div>
          <div className="mb-1 h-6 w-48 rounded bg-surface-elevated" />
          <div className="h-4 w-64 rounded bg-surface-elevated" />
        </div>
      </div>
      <div className="h-9 w-full max-w-md rounded bg-surface-elevated" />
      <div className="flex flex-1 flex-col gap-6 overflow-hidden lg:flex-row">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex-1 rounded-lg border border-default p-4">
            <div className="mb-3 h-4 w-20 rounded bg-surface-elevated" />
            <div className="space-y-2">
              <div className="h-16 rounded bg-surface-elevated" />
              <div className="h-16 rounded bg-surface-elevated" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Splits a task list into the three board columns by their (already
 * display-shaped) `TaskStatus`. Pure — no React, no I/O — so it can be
 * unit-tested directly without rendering the page.
 *
 * Pattern: docs/design-patterns/common/code-readability.md — small, named helper
 * Pattern: docs/design-patterns/anti-patterns/stringly-typed.md — keyed by TaskStatus, not raw strings
 */
export function groupTasksByColumn(tasks: TaskProps[]): Record<TaskStatus, TaskProps[]> {
  const groups: Record<TaskStatus, TaskProps[]> = {
    [TaskStatus.TODO]: [],
    [TaskStatus.IN_PROGRESS]: [],
    [TaskStatus.DONE]: [],
  };
  for (const task of tasks) {
    const status =
      (task.status as TaskStatus) in groups ? (task.status as TaskStatus) : TaskStatus.TODO;
    groups[status].push(task);
  }
  return groups;
}

/**
 * Breadcrumb trail for the task Kanban board: `Products` always; the parent
 * product (if resolvable) links to its project board; the project is the
 * leaf. When `product` is unresolved (still loading, or the project has no
 * linked product), the trail falls back to `Products › {project}` — never a
 * "Projects" crumb, since there is no standalone projects list page to link
 * to. Pure — no React, no I/O — so it can be unit-tested directly without
 * rendering the page.
 *
 * Pattern: docs/design-patterns/common/code-readability.md — small, named helper
 */
export function projectBreadcrumbs(
  product: Product | undefined,
  project: ProjectWithTasks | undefined,
): NonNullable<PageChrome['breadcrumbs']> {
  const crumbs: NonNullable<PageChrome['breadcrumbs']> = [{ label: 'Products', to: '/products' }];
  if (product) {
    crumbs.push({ label: product.name, to: `/products/${product.id}` });
  }
  if (project) {
    crumbs.push({ label: project.title });
  }
  return crumbs;
}

/**
 * Full-page task Kanban board: breadcrumb, project header (title + task
 * count), a search FilterBar, and a 3-column Board of TaskCards grouped by
 * status.
 *
 * Container — owns data fetching (useProject/useProduct/useTasks), drag-and-
 * drop (lifted from Projects.tsx's handleDragEnd), search/sort client state
 * (useTaskFiltering), and task/subtask mutations via useTaskOperations;
 * delegates rendering to shared Board/BoardColumn/TaskCard/FilterBar
 * components.
 * Pattern: docs/design-patterns/frontend/component-patterns.md
 */
export function ProjectView({ projectId: projectIdProp }: ProjectViewProps = {}): JSX.Element {
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = projectIdProp ?? projectIdParam ?? null;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const navigate = useNavigate();

  const { error: projectError, isLoading: projectLoading, project } = useProject(projectId);
  const { data: product } = useProduct(project?.productId ?? '');
  const { handleProjectUpdate } = useProjectOperations();
  const [showSpecDialog, setShowSpecDialog] = useState(false);

  const { editingTaskId } = useUIStore();
  const {
    handleSubtaskCreate,
    handleSubtaskDelete,
    handleSubtaskUpdate,
    handleTaskCreate,
    handleTaskDelete,
    handleTaskSelect,
    handleTaskUpdate,
  } = useTaskOperations(projectId);
  const { error: tasksError, isLoading: tasksLoading, tasks = [] } = useTasks(projectId);

  const chrome = useMemo<PageChrome>(
    () => ({
      title: project?.title ?? 'Project',
      breadcrumbs: projectBreadcrumbs(product, project ?? undefined),
      subtitle: pluralize(tasks.length, 'task'),
    }),
    [product, project, tasks],
  );
  useSetPageChrome(chrome);

  // Move focus to the project heading on route change (a new projectId), per
  // frontend-accessibility's SPA route-change focus rule.
  useEffect(() => {
    if (!projectLoading && project) {
      headingRef.current?.focus();
    }
  }, [projectLoading, project]);

  const tasksWithUI = useMemo<TaskProps[]>(
    () =>
      tasks.map(task => ({
        ...task,
        isEditing: task.id === editingTaskId,
        subtasks: task.subtasks.map(subtask => ({ ...subtask, isEditing: false })),
      })),
    [tasks, editingTaskId],
  );

  const {
    filteredTasks,
    searchQuery,
    setSearchQuery,
    setSortOption,
    shownCount,
    sortOption,
    totalCount,
  } = useTaskFiltering(tasksWithUI);

  const columns = useMemo(() => groupTasksByColumn(filteredTasks), [filteredTasks]);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      const taskId = result.draggableId;
      const newStatus = result.destination.droppableId as TaskStatus;
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        return;
      }

      logger.debug({ component: 'ProjectView' }, 'Drag ended', {
        taskId,
        currentStatus: task.status,
        newStatus,
      });

      void handleTaskUpdate(taskId, { status: newStatus });
    },
    [tasks, handleTaskUpdate],
  );

  if (projectLoading) {
    return <ProjectViewSkeleton />;
  }

  if (!project) {
    return (
      <div className={`p-6 ${MAIN_CONTENT_WIDTH}`}>
        <p className="text-sm text-error">
          {projectError instanceof Error ? 'Failed to load project.' : 'Project not found'}
        </p>
      </div>
    );
  }

  const hasSearchQuery = searchQuery.trim().length > 0;
  const noSearchMatches = totalCount > 0 && hasSearchQuery && shownCount === 0;

  return (
    <div className={`flex h-full flex-col gap-4 overflow-hidden p-6 ${MAIN_CONTENT_WIDTH}`}>
      <div className="flex items-start gap-3">
        <Button
          isIconOnly
          aria-label="Go back"
          size="sm"
          variant="light"
          onPress={() => void navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-secondary-800">
            Task board
          </p>
          <h1
            ref={headingRef}
            className="text-xl font-semibold text-foreground focus:outline-none"
            tabIndex={-1}
          >
            {project.title}
          </h1>
          {project.description && <p className="mt-1 text-sm text-muted">{project.description}</p>}
        </div>
      </div>

      <DocumentViewerDialog
        documentType="technical-spec"
        isOpen={showSpecDialog}
        onClose={() => setShowSpecDialog(false)}
        projectId={project.id}
        title={`Spec: ${project.title}`}
      />

      <FilterBar
        actions={
          <div className="flex items-center gap-2">
            <ProjectStatusSelect
              status={project.status}
              onStatusChange={status => handleProjectUpdate(project.id, { status })}
            />
            <Tooltip content="View project spec">
              <Button
                aria-label="View project spec"
                isIconOnly
                variant="light"
                onPress={() => setShowSpecDialog(true)}
              >
                <FileCode2 className="h-4 w-4" />
              </Button>
            </Tooltip>
          </div>
        }
        controls={
          <ButtonGroup size="sm">
            <Tooltip content="Sort by priority (High → Low)">
              <Button
                aria-label="Sort by priority"
                color={sortOption === 'priority' ? 'primary' : 'default'}
                isIconOnly
                onPress={() => setSortOption('priority')}
              >
                <ArrowUpDown className="h-4 w-4" />
              </Button>
            </Tooltip>
            <Tooltip content="Sort by newest first">
              <Button
                aria-label="Sort by newest first"
                color={sortOption === 'newest' ? 'primary' : 'default'}
                isIconOnly
                onPress={() => setSortOption('newest')}
              >
                <Clock className="h-4 w-4" />
              </Button>
            </Tooltip>
            <Tooltip content="Sort by oldest first">
              <Button
                aria-label="Sort by oldest first"
                color={sortOption === 'oldest' ? 'primary' : 'default'}
                isIconOnly
                onPress={() => setSortOption('oldest')}
              >
                <SortAsc className="h-4 w-4" />
              </Button>
            </Tooltip>
          </ButtonGroup>
        }
        count={{ shown: shownCount, total: totalCount }}
        searchPlaceholder="Search tasks..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {tasksError && (
        <div className="rounded-md border border-danger/40 bg-danger-100 p-3 text-sm text-danger-700 dark:bg-danger-200 dark:text-danger-800">
          Failed to load tasks. Please try again later.
        </div>
      )}

      {tasksLoading ? (
        <ProjectViewSkeleton />
      ) : noSearchMatches ? (
        <Feedback
          description="Try a different search term."
          title="No tasks match your search"
          type="no-results"
        />
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Board className="flex-1">
            {BOARD_STATUSES.map(status => {
              const { Icon, iconClass, label } = STATUS_DISPLAY[status];
              const columnTasks = columns[status];
              return (
                <Droppable key={status} droppableId={status}>
                  {provided => (
                    <BoardColumn
                      addLabel={`Add ${label.toLowerCase()} task`}
                      // @hello-pangea/dnd's droppableProps are DOM data-attributes, not a
                      // subset of React's HTMLAttributes — bridge the two library types
                      // the same way BoardColumn.test.tsx does for this dnd pass-through
                      // seam (see src/components/board/BoardColumn.test.tsx).
                      bodyProps={provided.droppableProps as HTMLAttributes<HTMLDivElement>}
                      bodyRef={provided.innerRef}
                      count={columnTasks.length}
                      label={label}
                      placeholder={provided.placeholder}
                      statusIcon={<Icon aria-hidden="true" className={iconClass} />}
                      onAdd={() => handleTaskCreate(status)}
                    >
                      {columnTasks.length === 0
                        ? // Per-column "add a task" empty state only makes sense for a
                          // genuinely empty column — during an active search, an empty
                          // column just means none of its tasks matched, which the
                          // board-level "No tasks match your search" state above
                          // already communicates (only reachable when every column is
                          // empty; a partial match leaves this column silently empty).
                          !hasSearchQuery && (
                            <p className="px-1 py-2 text-sm text-muted">No tasks</p>
                          )
                        : columnTasks.map((task, index) => (
                            <TaskCard
                              key={task.id}
                              index={index}
                              isLoadingSubtasks={false}
                              subtasksError={null}
                              task={task}
                              onDelete={handleTaskDelete}
                              onSelect={() => handleTaskSelect(task)}
                              onSubtaskAdd={handleSubtaskCreate}
                              onSubtaskDelete={handleSubtaskDelete}
                              onSubtaskUpdate={handleSubtaskUpdate}
                              onUpdate={(taskId, updates) => void handleTaskUpdate(taskId, updates)}
                            />
                          ))}
                    </BoardColumn>
                  )}
                </Droppable>
              );
            })}
          </Board>
        </DragDropContext>
      )}
    </div>
  );
}
